import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {BehaviorSubject, concat, forkJoin, fromEvent, isObservable, Observable, of, Subject, Subscription} from 'rxjs';
import {ListViewService} from './list-view-service.interface';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, Sort, SortDirection} from '@angular/material/sort';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DisplayedColumnsDialogComponent} from './displayed-columns-dialog/displayed-columns-dialog.component';
import {cloneDeep, find, isNil, remove, sortBy, unionBy} from 'lodash-es';
import {debounceTime, distinctUntilChanged, filter, first, map, skip, take, takeUntil} from 'rxjs/operators';
import {AuthenticationService} from '../../core/authentication.service';
import {MatExpansionPanel} from '@angular/material/expansion';
import {OverlayContainer} from '@angular/cdk/overlay';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {ListViewExportService} from './list-view-export.service';
import {NotificationService} from '../notification.service';
import {GoogleMapsModalComponent} from './google-maps-modal/google-maps-modal.component';
import {Location} from '@angular/common';
import {PermissionService} from 'src/app/core/permission.service';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {ListViewPreferencesService} from './list-view-preferences.service';
import {ShortcutInput} from 'ng-keyboard-shortcuts';
import {SelectionModel} from '@angular/cdk/collections';
import {TranslateService} from '@ngx-translate/core';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {HelpersService} from '../helpers.service';
import {MIHttpClientService} from '../mi-http-client.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {GenericDialogService} from '../generic-dialog.service';
import {ListViewHelperService} from './list-view-helper.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {ICustomAttributeProps} from "../../visits/builder/inputs/custom-attributes/customAttributes";
import {PreviewDialogComponent} from "../../viba/admin/dialog-flow/avatar/preview-dialog/preview-dialog.component";
import {environment} from "../../../environments/environment";
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';

export interface ListViewColumn<T> {
  columnDef: string;
  selected: boolean;
  isCustomAttribute?: boolean;
  customAttributeFieldType?: string
  cell?: (element: T) => string; //optional since for date and time it is computed based on timezone and value
  timezone?: (element: T) => string;
  value?: (element: T) => any;

  tooltip?: (element: T) => string;
  tooltipClass?: string;
  header: Observable<string>;
  sortColumnName?: string;
  sortable?: boolean;
  type?: 'text' | 'color' | 'status' | 'clickable' | 'icon' | 'link' | 'activeVisitStatus' | 'number' | 'date' | 'datetime' | 'time';
  color?: (element: T) => string;
}

export interface ListViewFilter {
  filterDef: string;
  filterLabel: Observable<string>;
  filterType?: string;
  filterValueById?: (id: string) => any;
  filterDefaultValue?: any;
  filterValueTransformer?: any;
}

export interface IBulkOperationSelection<T> {
  items?: Array<T>;
  itemsSelectedCount: number;
  allItemsSelected: boolean;
  filters?: any;
}

interface WithId {
  id: number;
}

export interface BulkOperationDefinition {
  label: Observable<string>;
  type: string;
}

export interface BulkOperationEvent<T> {
  bulkOperationSelection: IBulkOperationSelection<T>;
  type: string;
}

@Component({
    selector: 'list-view',
    templateUrl: './list-view.component.html',
    styleUrls: ['./list-view.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class ListViewComponent<T extends WithId> implements OnInit, OnDestroy, AfterViewInit {

  private destroyed$: Subject<void> = new Subject<void>();
  private listCallChanged$: Subject<void> = new Subject<void>();
  defaultColumns: ListViewColumn<T>[] = [];

  private _filters = '';
  private filtersSubject: BehaviorSubject<string> = new BehaviorSubject<string>(this.filters);
  private clearCacheRouteParam = false;

  private avatarPreviewDialogRef: MatDialogRef<any> | null = null;
  private avatarPreviewDialogSubscription: Subscription;

  // bulk operations variables
  selection = new SelectionModel<T>(true, []);
  @Input() public readonly withCheckBoxes: boolean;
  @Output('onImport') public importEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Input() public readonly importPermission: string;
  public withImportEnabled: boolean;
  @Input() public readonly bulkOperationsDefinitions: Array<BulkOperationDefinition>;
  @Output('bulkOperation') public bulkOperationEmitter: EventEmitter<BulkOperationEvent<T>>
    = new EventEmitter<BulkOperationEvent<T>>();
  bulkOperationsActive = false;
  public selectedItemsCount = 0; // in case user selects all filtered records( to be displayed on the modal)
  public allItemsSelected = false;
  public selectedItemsAll: Array<T> = []; // only to keep track of selected items when the page changes
  public currentSelectionItems: Array<T> = [];
  showSelectAllContainer = false;
  txtAllPageRecordsSelected = '';
  txtSelectAllRecords = '';
  txtAllRecordsSelected = '';
  showSelectAllItems = false;
  showClearAllItems = false;
  @Output('onClickable') public clickableEmitter: EventEmitter<T> = new EventEmitter<T>();
  @Output('displayedColumnsChanged') public displayedColumnsChanged: EventEmitter<ListViewColumn<T>[]> = new EventEmitter<ListViewColumn<T>[]>();
  isFilterPanelOpened = false;

  shortcuts: ShortcutInput[] = [];
  filtersExist = false;
  expandedElement = null;

  get filters(): string {
    return this._filters;
  }

  @Input()
  set filters(value: string) {
    this._filters = value;
    this.filtersSubject.next(value);
  }

  @Input() public readonly listIdentifier: string;
  @Input() public readonly title: string;
  @Input() public readonly cssClassName?: string;
  @Input() public readonly defaultSortColumn?: string;
  @Input() public readonly defaultSortDirection?: SortDirection;
  @Input() public readonly listViewFilters?: ListViewFilter[];
  @Input() public readonly customAttributeProps?: ICustomAttributeProps;

  private _columnsToTranslate: string[] = [
    "status",
    "Language",
    "mode",
    "eventType",
    "calendarEventTypeCategory",
    "currentStatus",
    "languageId",
    "languageName",
    "importType",
    "active"
  ]

  private _dateColumns: string[] = [
    "startDate",
    "endDate",
    "effectiveDate",
    "startVisitDate", //jobs, jobs batches
    "startDate",
    "endDate",
    "scheduledStartDate",
    "startDateTime", //projects
    "endDateTime",
    "EndDate", //OOO
    "CreatedOnDate", //forms
    "updatedOn",
    "createdOn",  //active-visits, all-visits, store-completed, adhoc completed, endpoints, content
    "importCreated", //imports
    "updatedOn", //endpoints, content
  ]

  private _numberColumns: string[] = [
    "visitFrequency",
    "futureVisitFrequency",
    "expectedDuration", //jobs
    "repsRequired", //job batches
    "progressTimeInHours", //active-visits
    "duration", //all-visits, store-completed, adhoc completed
    "numberOfIncomingCallsProject", //incoming calls list
    "numberOfIncomingCalls",
    "agentsAvailablePerProject",
    "agentsInCallPerProject",
    "activeCalls",
    "callHoldAverageTimeProject"
  ]

  private _initialColumns: ListViewColumn<T>[];
  @Input('columns')
  public set initialColumns(initialColumns: ListViewColumn<T>[]) {
    this._initialColumns = initialColumns;
    this.resetAllData();
  }

  private _defaultColumns: ListViewColumn<T>[];

  @Input('defColumns')
  public set defColumns(defColumns: ListViewColumn<T>[]) {
    this._defaultColumns = defColumns;
    //default columns can also change when switching teanants
    // if custom attributes are defined
    this.resetAllData();
  }

  @Input() public readonly listViewService: ListViewService<T>;
  @Input() public readonly clientSideFiltering = false;
  @Input() public readonly skipExportPagination = false;
  @Input() public readonly actionsTemplate: TemplateRef<T>;
  @Input() public readonly expandTemplate: TemplateRef<T>;
  @Input() public readonly maxActionCount: number = 4;
  @Input() public readonly localList: boolean = false; // false means data is paginated; true means no pagination
  @Input() public readonly canAdd: boolean = true;
  @Input() public readonly hasMap: boolean = false;
  @Input('columnsForMap') public readonly columnsForMap = [];
  @Input() public readonly iconTemplate: TemplateRef<T>;
  @Input() public readonly maxPageSize = 100; //Max page size supported by the API

  @Output('clearFilters') public clearFiltersEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output('add') public addEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output('expandRow') public expandRowEmitter: EventEmitter<void> = new EventEmitter<void>();

  public columns: ListViewColumn<T>[] = [];
  public dataSource: T[];
  public resultsLength = 0;

  displayedColumns$: Observable<string[]>;
  displayedColumns = [];
  cacheBuster = new Date().getTime();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  public pageSize = 10;
  public _pageIndex = 0;

  get pageIndex(): number {
    return this._pageIndex;
  }

  set pageIndex(val: number) {
    this._pageIndex = val;
  }

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatExpansionPanel) expansionPanel: MatExpansionPanel;

  @ViewChild('scrollable', { static: true }) scrollable: ElementRef<HTMLElement>;
  public atRightmostScroll = false;
  public isNil = (item: string) => isNil(item) || item === 'null';

  get areActionsAvailable(): boolean {
    return !isNil(this.actionsTemplate);
  }

  get isExpandRowAvailable(): boolean {
    return !isNil(this.expandTemplate);
  }

  @HostBinding('attr.style')
  get styleVariables(): SafeStyle {
    const perColumnWidth = 145;
    const perActionWidth = 35;
    this.displayedColumns$.subscribe(data => this.displayedColumns = data);
    return this.sanitizer.bypassSecurityTrustStyle(`
    --width-based-on-columns: ${this.displayedColumns.length * perColumnWidth}px;
    --actions-width: ${(this.maxActionCount + 1) * perActionWidth}px;
    `);
  }

  constructor(
    private dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private overlay: OverlayContainer,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public permissionService: PermissionService,
    public notificationService: NotificationService,
    private sanitizer: DomSanitizer,
    private exportService: ListViewExportService<T>,
    public listViewHelperService: ListViewHelperService,
    private location: Location,
    public preferencesService: ListViewPreferencesService<T>,
    public changeDetector: ChangeDetectorRef,
    private translateService: TranslateService,
    private helpersService: HelpersService,
    private miClient: MIHttpClientService,
    private genericDialogService: GenericDialogService,
    private breakpointObserver: BreakpointObserver,
  ) {

  }

  ngOnInit() {
    this.defaultColumns = cloneDeep(this._defaultColumns);

    if (this.activatedRoute.snapshot.params?.clearCache) {
      this.clearCacheRouteParam = true;
    }

    this.checkColumns();
    this.configureDefaultSorting();

    this.hookLiveReloadOnOrganizationChange();
    this.hookLiveReloadOnFilterChange();
    this.hookCloseFiltersOnOutsideClick();
    this.listViewHelperService.bulkOperationCompleted$.subscribe(data => {
      this.toggleBulkOperations();
      this.bulkOperationsActive = false;
    });

    this.withImportEnabled = this.importEmitter.observers.length > 0;
  }

  onClickable(row: T) {
    this.clickableEmitter.emit(row);
  }

  addCustomClass() {
    if (this.cssClassName) {
      return {
        [this.cssClassName]: true,
      };
    }
  }

  getDisplayedColumns(): string[] {
    if (isNil(this.columns)) {
      return;
    }
    const cols = cloneDeep(this.columns);
    let columns = cols
      .filter(column => column.selected)
      .map(column => column.columnDef);

    if (this.bulkOperationsActive) {
      if (columns[0] !== 'checkbox') {
        columns.unshift('checkbox');
        this.columns.unshift(
          { columnDef: 'checkbox', selected: true, header: null, cell: null },
        );
      }
    } else {
      if (columns[0] === 'checkbox') {
        columns.splice(0, 1);
      }
    }
    if (this.areActionsAvailable) {
      columns = columns.concat(['actions']);
    }

    if (this.isExpandRowAvailable) {
      columns = columns.concat(['expand']);
    }

    return columns;
  }

  toggleBulkOperations() {
    this.showSelectAllContainer = false;
    this.bulkOperationsActive = !this.bulkOperationsActive;
    if (!this.bulkOperationsActive) {
      this.deselectAllRecords();
    } else {
      if (this.columns[0].columnDef !== 'checkbox') {
        this.columns.unshift(
          {
            columnDef: 'checkbox',
            selected: true,
            cell: (element: any) => `${element.checked}`,
            header: null
          },
        );
      }

    }

    this.displayedColumns$ = of(this.getDisplayedColumns());
  }

  areAllItemsSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.length;
    return numRows > 0 && numSelected > 0 && numSelected === numRows;
  }

  public selectAllRecords() {

    this.allItemsSelected = true;
    this.selectedItemsCount = this.paginator.length;
    this.showSelectAllItems = false;
    this.showClearAllItems = true;

    if (this.clientSideFiltering) {
      const listViewOptions = {
        pageSize: `${this.selectedItemsCount}`,
        startIndex: '0',
        filters: this.filters,
      }
      this.listViewService.index(listViewOptions).subscribe(ret => {
        this.currentSelectionItems = ret.items;
      });
    } else {
      this.currentSelectionItems = [];
    }
  }

  public deselectAllRecords() {

    this.selection.clear();
    this.allItemsSelected = false;
    this.showSelectAllContainer = false;
    this.selectedItemsCount = 0;
    this.selectedItemsAll = [];
    this.currentSelectionItems = [];
  }

  public onCheckBoxHeaderChange(event: MatCheckboxChange) {
    // when checked, all filtered records are to be selected( when if not retrieved from server, yet)
    this.updatedSelectionOnHeaderToggle();
    this.selectedItemsCount = this.selection.selected.length;
    this.selectedItemsAll = unionBy(this.selection.selected, this.selectedItemsAll, 'id');
    this.currentSelectionItems = this.selection.selected;
    this.showSelectAllItems =  this.selectedItemsCount != this.paginator.length;
    this.showClearAllItems = false;

    this.translateService
      .stream(marker('All 111 records on this page are selected.'))
      .subscribe(data => this.txtAllPageRecordsSelected = data.replace('111', this.selectedItemsCount.toString()));
    this.translateService
      .stream(marker('Select all 111 records'))
      .subscribe(data => this.txtSelectAllRecords = data.replace('111', this.paginator.length.toString()));
    this.translateService
      .stream(marker('All 111 records are selected'))
      .subscribe(data => this.txtAllRecordsSelected = data.replace('111', this.paginator.length.toString()));
    this.showSelectAllContainer = true;

    if (!event.checked) {
      this.selection.clear();
      this.selectedItemsCount = 0;
      this.allItemsSelected = false;
      this.selectedItemsAll = [];
      this.currentSelectionItems = [];
      this.showSelectAllContainer = false;
    }
  }

  public onCheckBoxChange(event: MatCheckboxChange, row: T) {
    // when checked, only records from current page can be further checked/unchecked, the rest are considered unchecked
    this.selection.toggle(row);
    this.allItemsSelected = false;
    this.showSelectAllContainer = false;
    this.selectedItemsCount = this.selection.selected.length;

    if (event.checked) {
      this.selectedItemsAll = unionBy(this.selection.selected, this.selectedItemsAll, 'id');
      this.currentSelectionItems = unionBy(this.selection.selected, this.currentSelectionItems, 'id');
    } else {
      remove(this.selectedItemsAll, function (el) {
        return el.id === row.id;
      });
      remove(this.currentSelectionItems, function (el) {
        return el.id === row.id;
      });
    }
  }

  bulkOperation(type: string) {
    const bulkOperationSelection = this.getBulkParams();
    this.bulkOperationEmitter.emit({ bulkOperationSelection: bulkOperationSelection, type: type });
  }

  checkColumns() {
    const columnPreferences = this.preferencesService.getListColumnPreferences(this.listIdentifier);

    if (columnPreferences.length !== 0) {
      this.columns = this.updateSelectionAndOrdering(cloneDeep(this._initialColumns), columnPreferences);
    } else {
      this.columns = cloneDeep(this._defaultColumns);
    }
    this.displayedColumns$ = of(this.getDisplayedColumns());
  }

  exportExcel(): void {
    try {
      this.getExportData().pipe(take(1)).subscribe((data: T[]) => {
        this.exportService.exportAsExcel(data, this.title, {}, this.getExportColumns());
      });
    } catch (error) {
      this.notificationService.error(error.message);
    }
  }

  exportCsv(): void {
    try {
      this.getExportData().pipe(take(1)).subscribe((data: T[]) => {
        this.exportService.exportAsCsv(data, this.title);
      });
    } catch (error) {
      this.notificationService.error(error.message);
    }
  }

  updateRightmostScrollStatus(): void {
    const element: HTMLElement = this.scrollable.nativeElement;
    this.atRightmostScroll = element.scrollWidth - element.scrollLeft === element.clientWidth;
  }

  matSortChanged(event: Sort) {
    // Clear all selected items when user apply sorting
    this.selectedItemsAll = [];

    this.loadItems(false, null);
    this.sort.direction = event.direction;
    this.sort.active = event.active;
    // SAVE SORTINGPARAMS
    this.preferencesService.setSortingParamsToLocalStorage(this.listIdentifier, {
      orderBy: event.active,
      orderDirection: event.direction
    });
    this.paginator.firstPage();
  }

  loadItems(clearCache: boolean = false, event: PageEvent = null, complete?: (() => void) | null): void {
    const savedPageParams = this.preferencesService.loadSavedPageParams(this.listIdentifier);
    let isPageSizeChanged = false;

    if (savedPageParams !== null) {
      this.pageIndex = savedPageParams.pageIndex;
      this.pageSize = savedPageParams.pageSize;
      this.changeDetector.detectChanges();
    }

    if (event !== null) {
      if (this.pageSize !== event.pageSize) {
        isPageSizeChanged = true;
      }
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
      this.changeDetector.detectChanges();

      const newPageParams = {
        pageSize: this.pageSize,
        pageIndex: this.pageIndex,
      };
      this.preferencesService.setPageParametersToLocalStorage(this.listIdentifier, newPageParams);
    } else {
      this.pageIndex = this.paginator.pageIndex;
    }

    const pageSize = this.paginator.pageSize || this.pageSize;
    const startIndex = this.paginator.pageIndex * pageSize + 1;

    const orderBy = this.getSortColumnName(this.sort.active);
    const orderDirection = this.sort.direction || undefined;

    //stop previous index call
    this.listCallChanged$.next();
    this.listCallChanged$.complete();

    this.listCallChanged$ =  new Subject<void>();

    this.listViewService.index({
      pageSize: `${pageSize}`,
      startIndex: `${startIndex}`,
      orderBy: orderBy,
      orderDirection: orderDirection,
      filters: this.filters,
      clearCache,
    }).pipe(takeUntil(this.destroyed$), takeUntil(this.listCallChanged$)).subscribe(({ items, count }) => {
      if (isPageSizeChanged === true && this.areAllItemsSelected()) {
        this.selectedItemsAll = [...items];
      }

      if (isPageSizeChanged === true && !this.areAllItemsSelected()) {
        this.selectedItemsAll = [];
      }

      this.dataSource = items;

      this.onDataChanged(this.dataSource);
      this.resultsLength = count;
      this.updateRightmostScrollStatus();
      if (complete) {
        complete();
      }
    });
  }

  add() {
    this.addEmitter.emit();
  }

  clearFilters() {
    this.clearFiltersEmitter.emit();
  }

  selectDisplayedColumns() {
    this.dialog.open(DisplayedColumnsDialogComponent, {
      data: {
        columns: this.columns,
        initialColumns: this.columns,
        defaultColumns: this._defaultColumns,
        listIdentifier: this.listIdentifier,
      },
      width: '550px',
    }).afterClosed().subscribe((newColumns) => {
      if (newColumns) {
        if (this.bulkOperationsActive) {
          newColumns.unshift(
            {
              columnDef: 'checkbox',
              selected: true,
              cell: (element: any) => `${element.checked}`,
              header: null
            },
          );
        }
        this.columns = newColumns;
        this.displayedColumns$ = of(this.getDisplayedColumns());
        this.preferencesService.setColumnsToLocalStorage(this.listIdentifier, this.columns);
        this.displayedColumnsChanged.emit(newColumns);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  showMap(): void {
    this.getMapData().pipe(take(1)).subscribe((data: T[]) => {
      this.genericDialogService.open(GoogleMapsModalComponent, {
        data: data,
      });
    });
  }

  get filtersList() {
    if (this.filters) {
      let filters;

      if (this.filters.match(/^\?/)) {
        filters = this.queryStringToObject(this.filters);
      } else {
        filters = JSON.parse(this.filters);
      }

      return this.filtersObjectToList(filters);
    }
  }

  filterLabel(filterDef) {
    return this.listViewFilters.find((item) => item.filterDef === filterDef)?.filterLabel;
  }

  ngAfterViewInit() {
    this.shortcuts.push(
      {
        key: ['ctrl + n'],
        label: 'List',
        description: 'Open create modal',
        command: () => this.add(),
      },
      {
        key: ['ctrl + f'],
        label: 'List',
        description: 'Toggle filters drop-down',
        command: () => this.expansionPanel.toggle(),
      },
    );
  }

  private updateSelectionAndOrdering(latestColumns: ListViewColumn<T>[], persistedColumns: { columnDef: string, selected: boolean }[]): ListViewColumn<T>[] {
    const reorderedColumns = [];
    //consider order from persisted columns (saved preferences)
    persistedColumns.forEach(persistedColumn => {
      const initialColumn = latestColumns.find(
        column => column.columnDef === persistedColumn.columnDef);

      if (initialColumn !== undefined) {
        //still have the column
        initialColumn.selected = persistedColumn.selected;
        reorderedColumns.push(initialColumn);
      }
    });

    //check new columns added after preference save
    latestColumns.forEach(latestColumn => {
      const persistedColumn = persistedColumns.find(
        column => column.columnDef === latestColumn.columnDef);
      if (persistedColumn === undefined) {
        //added after preference save
        reorderedColumns.push(latestColumn);
      }
    });

    return reorderedColumns;
  }

  private updatedSelectionOnHeaderToggle() {
    this.areAllItemsSelected() ?
      this.selection.clear() :
      this.dataSource.forEach(row => this.selection.select(row));
  }

  private getExportData(): Observable<object[]> {

    const exportColumns = this.getExportColumns();

    const listElements$: Observable<object[]> = this.miClient.getAllListElements(this.resultsLength,
      this.maxPageSize,
      this.sort.active, this.sort.direction || undefined,
      this.filters,
      this.listViewService, this.skipExportPagination);

    const columnHeaders$ = forkJoin(
      exportColumns
        .map((column: ListViewColumn<T>) => column.header.pipe(take(1))),
    ).pipe(
      map((values: string[]) => values.reduce((acc, header: string | string[], index: number) => {

        let headerValue = header;
        if (Array.isArray(headerValue)) {
          headerValue = headerValue[0];
        }

        acc[exportColumns[index].columnDef] = headerValue;
        return acc;
      }, {})),
    );

    return forkJoin([listElements$, columnHeaders$]).pipe(
      map(([listElements, columnHeaders]) => listElements.map(
        entry => this.filterToExportValues(entry, exportColumns, columnHeaders),
      )));

  }

  private getExportColumns(): ListViewColumn<T>[] {
    const exportColumns = cloneDeep(this.columns);
    // remove checkboxes
    if (exportColumns[0].columnDef === 'checkbox') {
      exportColumns.splice(0, 1);
    }

    //only selected
    return exportColumns.filter((column: ListViewColumn<T>) => column.selected);
  }

  private getMapData(): Observable<object[]> {

    const mapColumns = this.getMapColumns();

    return this.miClient.getAllListElements(this.resultsLength,
      this.maxPageSize,
      this.sort.active, this.sort.direction || undefined,
      this.filters,
      this.listViewService, this.skipExportPagination)
      .pipe(
        map((result: T[]) => result.map(
          entry => this.filterToColumnDefCellValues(entry, mapColumns),
        )),
      );
  }

  private getMapColumns(): ListViewColumn<T>[] {
    const mapColumns = cloneDeep(this.columns);

    //only selected
    return mapColumns.filter((column: ListViewColumn<T>) => this.columnsForMap.includes(column.columnDef));
  }

  private filterToColumnDefCellValues<T>(element: T, filterColumns: ListViewColumn<T>[]): any {
    return filterColumns
      .reduce((acc, column: ListViewColumn<T>) => {
        if(this._columnsToTranslate.includes(column.columnDef) || column.cell && column.cell(element) == "All" || column.cell && column.cell(element) == "Default") {
          this.translateService.stream(marker(column.cell(element))).subscribe((cellValue) => {
            acc[column.columnDef] = cellValue
          })
        } else
          acc[column.columnDef] = column.cell(element)
        return acc;
      }, {});
  }


  private filterToExportValues<T>(element: T, filterColumns: ListViewColumn<T>[], columnHeaders: any): any {
    return filterColumns
      .reduce((acc, column: ListViewColumn<T>) => {

        const columnHeader = columnHeaders[column.columnDef];

        if(this._columnsToTranslate.includes(column.columnDef) || column.cell && column.cell(element) == "All" || column.cell && column.cell(element) == "Default") {
          this.translateService.stream(marker(column.cell(element))).subscribe((cellValue) => {
            acc[columnHeader] = cellValue
          })
        } else {
          acc[columnHeader] = this.exportValue(column, element);
        }

        return acc;
      }, {});
  }


  private getBulkParams(): IBulkOperationSelection<T> {
    let filters: string;
    if (this._filters === '{}' || this._filters === '') {
      filters = '';
    } else if (!isNil(this._filters) && !this.filters.startsWith('?') && !(this._filters.trim() === '')) {
      const filterObj = JSON.parse(this._filters);
      filters = `?${Object.keys(filterObj).map(key => `${key}=${filterObj[key]}`).join('&')}`;
    } else {
      filters = this._filters;
    }
    return {
      items: this.currentSelectionItems,
      allItemsSelected: this.allItemsSelected,
      itemsSelectedCount: this.selectedItemsCount,
      filters: filters,
    };
  }

  private onDataChanged(items: Array<T>) {
    // if we return to a page with selected items, keep them in the selection
    this.showSelectAllContainer = false;
    this.currentSelectionItems = [];
    this.selection.clear();
    this.selectedItemsCount = 0;

    items.forEach(row => {
      if (find(this.selectedItemsAll, row)) {
        this.selection.select(row);
      }
    });
    this.currentSelectionItems = this.selection.selected;
    this.selectedItemsCount = this.selection.selected.length;
  }

  // to show the sorting marker for non local Lists
  private getSortColumnOrField(sortField: string) {
    if (!this.localList) {
      return this.columns.filter(item => item.sortColumnName === sortField)[0]?.columnDef;
    }
    return sortField;
  }

  private hookCloseFiltersOnOutsideClick(): void {
    fromEvent(document, 'mousedown')
      .pipe(
        filter(({ target }) => !this.expansionPanel._body.nativeElement.parentElement.contains(target as HTMLElement)),
        filter(({ target }) => !this.overlay.getContainerElement().contains(target as HTMLElement)),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.expansionPanel.close();
      });
  }

  private hookLiveReloadOnOrganizationChange(): void {

    this.authenticationService.activeOrganizationInfo$
      .pipe(
        skip(1),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.checkColumns();
        this.activatedRoute.params.subscribe(params => {
          if (params['clearCache'] === 'true') {
            this.loadItems(true);
            const parts = this.router.url.split(';');
            this.location.replaceState(parts[0]);
          } else {
            this.loadItems(this.clearCacheRouteParam);
          }
        });
      });
  }

  private hookLiveReloadOnFilterChange(): void {
    concat(
      this.filtersSubject.asObservable().pipe(first()),
      this.filtersSubject.asObservable().pipe(skip(1), debounceTime(1000)),
    ).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroyed$),
    ).subscribe((filters: string) => {
      this.paginator.firstPage();
      this.updateQueryParams(filters);
    });
  }

  private updateQueryParams(filters: string): void {
    let queryParams: Params = {};

    if (this.localList) {
      queryParams = JSON.parse(filters);
    } else {
      new URLSearchParams(filters.replace('?', ''))
        .forEach((value, key) => {
          if (Object.keys(queryParams).includes(key)) {
            queryParams[key] = value + ',' + queryParams[key];
          } else {
            queryParams[key] = value;
          }
        });
    }

    if (Object.keys(queryParams).length > 0) {
      this.filtersExist = true;
    } else {
      this.filtersExist = false;
    }

    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams,
      }).then(() => {
        // Reload data only after new route changed
        this.checkColumns();
        this.loadItems(this.clearCacheRouteParam);
      }
    );

    // Clear all selected items when user change the filters
    this.selectedItemsAll = [];
  }

  private resetAllData() {
    // this.defaultColumns = cloneDeep(this._initialColumns);
    this.checkColumns();
    this.dataSource = [];

    // Clear all selected items when user change the filters
    this.selectedItemsAll = [];

    if (this.listViewService !== undefined) {
      this.loadItems(true);
    }
  }

  private configureDefaultSorting() {

    const savedSortingParams = this.preferencesService.loadSavedSortingParams(this.listIdentifier);
    if (savedSortingParams !== null && Object.keys(savedSortingParams).length > 0) {

      this.sort.active = savedSortingParams.orderBy;
      this.sort.direction = savedSortingParams.orderDirection as SortDirection;

      return;
    }
    if (this.defaultSortColumn !== null && this.defaultSortColumn !== undefined) {
      this.sort.active = this.defaultSortColumn;
      this.sort.direction = this.defaultSortDirection;
    }
  }

  private getSortColumnName(columnDef: string): string {
    return this._initialColumns.find(column => column.columnDef === columnDef)?.sortColumnName || columnDef;
  }

  private queryStringToObject(queryString: string) {
    const params = queryString.replace('?', '').split('&');
    const object = {};

    params.forEach((param) => {
      const keyValue = param.split('=');
      const key = keyValue[0];
      const value = keyValue[1];

      let existingValue = object[key];
      if (existingValue) {
        if (Array.isArray(existingValue)) {
          existingValue.push(value);
        } else {
          existingValue = [existingValue, value];
        }

        object[key] = existingValue;
      } else {
        object[key] = value;
      }
    });

    return object;
  }

  private filtersObjectToList(filters: any) {
    const filtersList = [];

    for (const [filterDef, rawValue] of Object.entries(filters)) {
      const columnFilter = this.getFilter(filterDef);

      if (columnFilter) {
        const filterValues = this.getFilterValues(rawValue, columnFilter);
        const filterOrder = this.getFilterOrder(columnFilter);

        filtersList.push({
          filterName: columnFilter.filterLabel,
          filterValues: filterValues,
          filterOrder: filterOrder,
        });
      }
    }

    const customAttributesFilters = this.customAttributeProps?.filters;

    if (customAttributesFilters) {
      Object.keys(customAttributesFilters).forEach((key, index) => {
        if (customAttributesFilters[key].filterValues) {
          filtersList.push({
            filterName: of(customAttributesFilters[key].filterName),
            filterValues: [of(customAttributesFilters[key].filterValues)],
            filterOrder: customAttributesFilters[key].filterOrder,
            symbol: customAttributesFilters[key].symbol,
            userAttrFilter: true
          });
        }
      });
    }

    return sortBy(filtersList, (item) => item.filterOrder);
  }

  private getFilter(filterDef: string): ListViewFilter {
    if (this.listViewFilters) {
      return this.listViewFilters.find((item) => item.filterDef === filterDef);
    }
  }

  private getFilterOrder(columnFilter: ListViewFilter): number {
    return this.listViewFilters.indexOf(columnFilter);
  }

  private getFilterValues(rawValue: any, columnFilter: ListViewFilter) {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return [];
    }

    let decodedValue = decodeURIComponent(rawValue);

    if (typeof columnFilter?.filterValueById === 'function') {
      return decodedValue.toString().split(',').map((item) => {
        const filterValue = columnFilter.filterValueById(item);

        return isObservable(filterValue) ? filterValue : of(filterValue);
      });
    } else {
      if (columnFilter.filterType === 'date') {
        decodedValue = this.helpersService.formatDateDatePicker(decodedValue);
      }

      return Array.isArray(decodedValue) ? decodedValue.map((item) => of(item)) : [of(decodedValue)];
    }
  }

  addHttpProtocol(url: string): string {
    if (!isNil(url) && !url.startsWith('http')) {
      return 'http://' + url;
    }
    return url;
  }

  notEmpty(url: string) {
    return !isNil(url);
  }

  getColor(col, row): string {
    return typeof col.color === 'function' ? col.color(row) : '';
  }

  dropHeader(event: CdkDragDrop<T, any>) {
    const newColumns = this.columns.filter(item => item.columnDef !== 'checkbox');
    const visible = newColumns.filter(({ selected }) => selected);
    const previousIndex = newColumns.indexOf(visible[event.previousIndex]);
    const currentIndex = newColumns.indexOf(visible[event.currentIndex]);
    moveItemInArray(newColumns, previousIndex, currentIndex);
    this.columns = newColumns;
    this.displayedColumns$ = of(this.getDisplayedColumns());
    this.preferencesService.setColumnsToLocalStorage(this.listIdentifier, this.columns);
    this.displayedColumnsChanged.emit(this.columns);
  }

  onClickRow(row) {
    this.expandedElement = this.expandedElement === row ? null : row;

    if (this.expandedElement) {
      this.expandRowEmitter.emit(this.expandedElement);
    }
  }

  triggerImport() {
    this.importEmitter.emit();
  }

  openCustomAttributes() {
    this.router.navigate([this.customAttributeProps.editAttributesRoute])
  }

  cellValue<T>(column: ListViewColumn<T>, row: T) {
    if (column.cell) {
      return column.cell(row);
    }

    if (column.type === 'datetime') {
      let timezone = column.timezone ? column.timezone(row): null;
      let value = column.value(row);

      return this.helpersService.formatDateInTz(value, timezone) + ' ' + this.helpersService.formatTimeInTz(value, timezone);
    }

    if (column.type === 'date') {
      let timezone = column.timezone ? column.timezone(row): null;
      let value = column.value(row);

      return this.helpersService.formatDateInTz(value, timezone);
    }

    if (column.type === 'time') {
      let timezone = column.timezone ? column.timezone(row): null;
      let value = column.value(row);

      return this.helpersService.formatTimeInTz(value, timezone);
    }

  }

  private exportValue<T>(column: ListViewColumn<T>, element: T): any {
    let cellValue = this.cellValue(column, element);

    if(cellValue === null || cellValue === "null" || cellValue === undefined || cellValue === "" || cellValue === "-" ) {
      return ''
    }

    else if(column.type == 'date' || column.customAttributeFieldType=="DATE_PICKER_FIELD") {
      let timezone = column.timezone ? column.timezone(element) : null;
      let value = column.value(element);

      return this.helpersService.formatExportDateInTz(value, timezone);
    }

    else if(column.type == 'datetime') {
      let timezone = column.timezone ? column.timezone(element) : null;
      let value = column.value(element);

      return this.helpersService.formatExportDateTimeInTz(value, timezone);
    }

    else if(column.type == 'time') {
      let timezone = column.timezone ? column.timezone(element) : null;
      let value = column.value(element);

      return this.helpersService.formatExportTimeInTz(value, timezone);
    }


    else if(column.type == 'number' || column.customAttributeFieldType=="NUMBER_FIELD") {
      return +cellValue
    }

    else {
      return cellValue;
    }
  }

  onThumbnailClick(name: string): void {
    const url = `${environment.micrositeBaseUrl}/debug/ai-text?avatarName=${name}`;

    this.avatarPreviewDialogSubscription = this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
      ])
      .subscribe((result) => {
        let width = '22%';

        if (result.breakpoints[Breakpoints.XSmall]) {
          width = '100%';
        } else if (result.breakpoints[Breakpoints.Small]) {
          width = '50%';
        } else if (result.breakpoints[Breakpoints.Medium]) {
          width = '35%';
        }

        if (this.avatarPreviewDialogRef) {
          this.avatarPreviewDialogRef.close();
        }

        this.avatarPreviewDialogRef = this.dialog.open(PreviewDialogComponent, {
          width: width,
          data: {
            title: 'Avatar preview',
            url: url
          },
        });
      });

    this.avatarPreviewDialogRef.afterClosed().subscribe(() => {
      if (this.avatarPreviewDialogSubscription) {
        this.avatarPreviewDialogSubscription.unsubscribe();
        this.avatarPreviewDialogSubscription = null;
      }
    });
  }
}
